import { useFirebase } from '@/firebase';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { logger } from '@/lib/logger';
import { ContentDocument } from '@/lib/types';

/**
 * Hook to manage text mutations (create, rename, delete).
 * Primarily uses TextDao for summary mirroring.
 */
export function useTextActions() {
  const { contentDao, novel, getNewId } = useNovelRepository();
  const { user } = useFirebase();
  const { toast } = useToast();

  const createNewText = async (name: string, sceneIds: string[] = [], initialContent = '') => {
    if (!contentDao || !novel.id || !user || !getNewId) return null;

    try {
      const id = getNewId();
      const newDoc: ContentDocument = {
        id,
        name,
        scratchpad: initialContent,
        scratchpadVersion: 0,
        currentDayKey: new Date().toISOString().split('T')[0],
        sceneIds,
        wordCount: initialContent.trim() ? initialContent.replace(/<[^>]*>/g, ' ').trim().split(/\s+/).length : 0,
        audit: {
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      };

      await contentDao.createContent(novel.ownerId, novel, newDoc);
      toast({ title: 'New prose document created' });
      return id;
    } catch (err) {
      logger.error('Failed to create content:', err);
      toast({ title: 'Error creating prose document', variant: 'destructive' });
      return null;
    }
  };

  const renameText = async (contentId: string, newTitle: string) => {
    if (!contentDao || !novel.id || !user) return;
    try {
      await contentDao.updateContent(novel.ownerId, novel, contentId, { name: newTitle });
    } catch (err) {
      logger.error('Failed to rename content:', err);
      toast({ title: 'Error renaming prose document', variant: 'destructive' });
    }
  };

  const updateSceneAssociations = async (contentId: string, sceneIds: string[]) => {
    if (!contentDao || !novel.id || !user) return;
    try {
      await contentDao.updateContent(novel.ownerId, novel, contentId, { sceneIds });
    } catch (err) {
      logger.error('Failed to update scene associations:', err);
      toast({ title: 'Error updating scene links', variant: 'destructive' });
    }
  };

  const deleteTextDoc = async (contentId: string) => {
    if (!contentDao || !novel.id || !user) return;
    try {
      await contentDao.deleteContent(novel.ownerId, novel, contentId);
      toast({ title: 'Document deleted' });
    } catch (err) {
      logger.error('Failed to delete content:', err);
      toast({ title: 'Error deleting document', variant: 'destructive' });
    }
  };

  return {
    createNewText,
    renameText,
    updateSceneAssociations,
    deleteTextDoc,
  };
}
